import { GoldenSourceHelper } from "../../../app/Switch_Core_React_Standards";
import { dbPayload } from "../../../auth/registered/SEC_00_DB_Payload";
import { registerNewBatch, deliverFiles } from "../../upload/services/batchActions";
import { stageFilesLocally, finalizeLocalBatch } from "../../upload/services/storageService";

/**
 * 🛰️ KNOWLEDGE BASE — interaction service
 * ----------------------------------------------------------------------------
 * CRUD  : SUSTINA DB gateway (dbPayload + GoldenSourceHelper), proxied by the
 *         BFF straight through to nrgCore -> ESG.usp_KnowledgeDocument_* in RDB2.
 *         See .claude/db/create_knowledge_base.sql.
 *
 * Parse : the sustina-esg-xbrl-processor Function-URL Lambda, action
 *         INGEST_KNOWLEDGE_DOCUMENT (lambda/sustina_esg_xbrl_processor/handler.py).
 *         Routed by the BFF Doorman as intent "IngestKnowledgeDocument" so it
 *         dodges API Gateway's 29s cap (big PDF / Arelle DTS load). It returns
 *         extracted text (+ XBRL facts) and writes NOTHING — Sustina keeps only
 *         the text, in SQL.
 *
 * File  : the picked file is staged into esg-staging-dirty via the existing
 *         batch upload pipeline (knowledge-intake/ prefix, expired by an S3
 *         lifecycle rule), then the Lambda reads it back by {bucket,key}.
 */

const ROOT = "/api/v1/sustina/knowledge-base";
const DIRTY_BUCKET = process.env.REACT_APP_SITE_DIRTY_BUCKET || "esg-staging-dirty";
const INTAKE_PREFIX = "knowledge-intake/";

const post = async (path, body, config, intent = "") => {
  const raw = await dbPayload(
    `${ROOT}${path}`,
    "POST",
    config.siteKey,
    GoldenSourceHelper.prepareForApi(body || {}),
    intent,
  );
  return GoldenSourceHelper.normalize(raw);
};

// ---------------------------------------------------------------------------
// Ref pick-lists
// ---------------------------------------------------------------------------

export const fetchDocumentTypes = async (config) => {
  try {
    const res = await post("/document-type/list", {}, config);
    return res.success ? res.data || [] : [];
  } catch (err) {
    console.error("Knowledge Base — document type list failed:", err);
    return [];
  }
};

export const fetchDocumentStatuses = async (config) => {
  try {
    const res = await post("/document-status/list", {}, config);
    return res.success ? res.data || [] : [];
  } catch (err) {
    console.error("Knowledge Base — document status list failed:", err);
    return [];
  }
};

// ---------------------------------------------------------------------------
// CRUD
// ---------------------------------------------------------------------------

export const fetchKnowledgeDocuments = async (filters = {}, config) => {
  try {
    const res = await post(
      "/list",
      {
        CategoryTag: filters.categoryTag || null,
        DocumentTypeCode: filters.documentTypeCode || null,
        StatusCode: filters.statusCode || null,
        IncludeInactive: filters.includeInactive ? 1 : 0,
      },
      config,
    );
    return res.success ? res.data || [] : [];
  } catch (err) {
    console.error("Knowledge Base — list failed:", err);
    return [];
  }
};

export const getKnowledgeDocument = async (knowledgeDocumentId, config) => {
  try {
    const res = await post("/get", { KnowledgeDocumentId: knowledgeDocumentId }, config);
    if (!res.success) return null;
    const d = res.data || {};
    return d.document ? { ...d.document, chunks: d.chunks || [] } : { ...d, chunks: d.chunks || [] };
  } catch (err) {
    console.error("Knowledge Base — get failed:", err);
    return null;
  }
};

/**
 * 🛰️ ADD a document (+ its passages) in one call.
 * Mirrors precmed-engine DocumentRepository.InsertDocumentWithChunksAsync.
 * @param {object} payload  { Name, DocumentTypeCode, CategoryTag, EffectiveDate,
 *   SourceKind, SourceUrl, OriginalFileName, MimeType, SourceFormat, ContentSha256,
 *   ParserName, ParserVersion, RawText, StatusCode, Chunks[] }
 */
export const addKnowledgeDocument = async (payload, config) => {
  const res = await post(
    "/add",
    {
      Name: payload.Name,
      DocumentTypeCode: payload.DocumentTypeCode,
      CategoryTag: payload.CategoryTag || null,
      EffectiveDate: payload.EffectiveDate || null,
      SourceKind: payload.SourceKind || "text",
      SourceUrl: payload.SourceUrl || null,
      OriginalFileName: payload.OriginalFileName || null,
      MimeType: payload.MimeType || null,
      SourceFormat: payload.SourceFormat || null,
      ContentSha256: payload.ContentSha256 || null,
      ParserName: payload.ParserName || null,
      ParserVersion: payload.ParserVersion || null,
      RawText: payload.RawText || null,
      StatusCode: payload.StatusCode || "INDEXED",
      ChunksJson: JSON.stringify(payload.Chunks || []),
    },
    config,
  );
  if (!res.success) throw new Error(res.message || "Could not register the document.");
  return res.data;
};

export const updateKnowledgeDocument = async (knowledgeDocumentId, payload, config) => {
  const res = await post(
    "/update",
    {
      KnowledgeDocumentId: knowledgeDocumentId,
      Name: payload.Name,
      DocumentTypeCode: payload.DocumentTypeCode,
      CategoryTag: payload.CategoryTag || null,
      EffectiveDate: payload.EffectiveDate || null,
      StatusCode: payload.StatusCode || null,
    },
    config,
  );
  if (!res.success) throw new Error(res.message || "Could not update the document.");
  return res.data;
};

export const removeKnowledgeDocument = async (knowledgeDocumentId, config) => {
  const res = await post("/remove", { KnowledgeDocumentId: knowledgeDocumentId }, config);
  if (!res.success) throw new Error(res.message || "Could not remove the document.");
  return true;
};

export const replaceKnowledgeDocumentChunks = async (knowledgeDocumentId, chunks, config) => {
  const res = await post(
    "/chunk/replace",
    { KnowledgeDocumentId: knowledgeDocumentId, ChunksJson: JSON.stringify(chunks || []) },
    config,
  );
  if (!res.success) throw new Error(res.message || "Could not rebuild passages.");
  return res.data;
};

// ---------------------------------------------------------------------------
// Parse (sustina-esg-xbrl-processor Lambda, via BFF intent)
// ---------------------------------------------------------------------------

/**
 * 🛰️ EXTRACT text from one source (URL, or a file already staged in S3).
 * @param {object} source { sourceKind:"url"|"file"|"xbrl", sourceUrl?, bucket?, key?,
 *                           mimeHint?, originalFileName?, entryPointPath? }
 * @returns {Promise<{ text, title, format, mimeType, parserName, parserVersion,
 *                     sha256, byteSize, facts?, factCount? }>}
 */
export const extractKnowledgeSource = async (source, config) => {
  // The Lambda dispatches on the top-level "action" and reads its inputs from
  // "payload" (see handler.py _parse_body / handle_ingest_knowledge). Same
  // { action, payload } shape the taxonomy/filing callers use. The BFF relays
  // this verbatim (Doorman intent "IngestKnowledgeDocument" -> ExecuteXbrlAsync);
  // dbPayload also braids siteUserGuid/sessionID in at the top level, which the
  // Lambda ignores.
  const res = await post(
    "/extract",
    {
      action: "INGEST_KNOWLEDGE_DOCUMENT",
      payload: {
        sourceKind: source.sourceKind,
        sourceUrl: source.sourceUrl || null,
        bucket: source.bucket || null,
        key: source.key || null,
        mimeHint: source.mimeHint || null,
        originalFileName: source.originalFileName || null,
        entryPointPath: source.entryPointPath || null,
      },
    },
    config,
    "IngestKnowledgeDocument",
  );
  if (!res.success) throw new Error(res.message || "Could not read that source.");
  const d = res.data || {};
  return {
    text: d.text || d.Text || "",
    title: d.title || d.Title || "",
    format: d.format || d.Format || "",
    mimeType: d.mimeType || d.MimeType || "",
    parserName: d.parserName || d.ParserName || "",
    parserVersion: d.parserVersion || d.ParserVersion || "",
    sha256: d.sha256 || d.Sha256 || "",
    byteSize: d.byteSize || d.ByteSize || 0,
    facts: d.facts || d.Facts || null,
    factCount: d.factCount || d.FactCount || 0,
  };
};

/**
 * Stage a picked file into esg-staging-dirty/knowledge-intake/ using the same
 * batch pipeline the upload workspace uses, and return its {bucket, key}.
 * The object is transient — an S3 lifecycle rule on that prefix expires it.
 */
export const stageKnowledgeFile = async (file, config) => {
  const siteKey = config.siteKey || process.env.REACT_APP_SITE_KEY;
  const safeName = `${Date.now()}_${file.name}`.replace(/[^\w.-]+/g, "_");
  const metaData = {
    S3CorsValue: "esg",
    targetBucket: DIRTY_BUCKET,
    targetRoot: INTAKE_PREFIX,
    batchType: 2,
    versionLabel: "KnowledgeBase intake",
  };

  const batch = await registerNewBatch(
    [new File([file], safeName, { type: file.type || "application/octet-stream" })],
    { siteKey, batchType: 2 },
    metaData,
  );
  const staged = [new File([file], safeName, { type: file.type || "application/octet-stream" })];
  await stageFilesLocally(staged, batch.batchId, siteKey);
  await finalizeLocalBatch(batch.batchId, siteKey, batch.hash, batch.timestamp, metaData);
  await deliverFiles(batch.batchId, staged, siteKey);

  return { bucket: DIRTY_BUCKET, key: `${INTAKE_PREFIX}${safeName}` };
};
